import React, { useContext, useEffect, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { Checkbox, Image, Loader, Table } from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/AccommodationsTable.less';

import iconInfo from '../img/icon-info.svg';

import { SatCoreRegister } from '../SatCoreRegistry';

import AccommodationsService from '../services/AccommodationsService';
import PopupService from '../services/PopupService';
import { ACCOMMODATION_TOGGLE_KEY } from '../services/AccommodationConstants';
import TranslationLanguageSelector, { translationLanguageNames } from './TranslationLanguageSelector';

const AccommodationsTable = observer((props) => {
  const { /* hideEdit, readOnly, */t } = props;

  const {
    accommodationsManager
  } = useContext(MobXProviderContext);

  const [hideTable, setHideTable] = useState(false);
  const [loadingAccommodationsTable, setLoadingAccommodationsTable] = useState(false);
  const [translationLanguageSelections, setTranslationLanguageSelections] = useState(new Map());
  const [translationLanguageNamesMap, setTranslationLanguageNamesMap] = useState(translationLanguageNames);
  const [localeNamesMap, setLocaleNamesMap] = useState({});
  const [hasC2CTranslate, setHasC2CTranslate] = useState(false);

  // Fetch publisher options to determine if translation language column should be shown
  useEffect(() => {
    const loadPublisherOptions = async () => {
      const options = await AccommodationsService.fetchPublisherOptions();
      if (options) {
        // C2C Translate is available when the publisher has AI enabled
        setHasC2CTranslate(!!options.aiEnabled);
      }
    };
    loadPublisherOptions();
  }, []);

  // Fetch dynamic translation language names (only if C2C Translate is available)
  useEffect(() => {
    if (!hasC2CTranslate) {
      return;
    }

    const loadTranslations = async () => {
      const languages = await AccommodationsService.fetchTranslationLanguages();
      if (languages && Object.keys(languages).length > 0) {
        setTranslationLanguageNamesMap(languages);
      }
    };
    loadTranslations();
  }, [hasC2CTranslate]);

  // Fetch dynamic locale names (only if C2C Translate is available)
  useEffect(() => {
    if (!hasC2CTranslate) {
      return;
    }

    const loadLocales = async () => {
      const locales = await AccommodationsService.fetchLocaleNames();
      if (locales && Object.keys(locales).length > 0) {
        setLocaleNamesMap(locales);
      }
    };
    loadLocales();
  }, [hasC2CTranslate]);

  const config = AccommodationsService.ACCOMMODATIONS_CONFIG();

  const handleToggleAccommodation = async (_event, { checked }, {
    rosterItem,
    sectionKey,
    toggleKey
  } = {}) => {
    setLoadingAccommodationsTable(true);

    const showingAllSections = config.assessmentSection?.show && config.lessonSection?.show;
    const shouldRefreshTableData = !rosterItem || (showingAllSections && config.treatAllSectionsAsOne);

    if (shouldRefreshTableData) {
      setHideTable(true);
    }
    const shouldRemove = !checked;
    const sectionKeys = config.treatAllSectionsAsOne ? ['assessmentSection', 'lessonSection'] : [sectionKey];
    await updateStudentOrClassroomAccommodationOption({ rosterItem, sectionKeys, shouldRemove, toggleKey });

    if (!hideTable) {
      setHideTable(true);
    }
    if (shouldRefreshTableData) {
      setTimeout(async () => {
        await accommodationsManager.fetchAccommodationsRoster();
        setLoadingAccommodationsTable(false);
        setHideTable(false);
      }, 1500);
    } else {
      setLoadingAccommodationsTable(false);
      setHideTable(false);
    }
  };

  const updateStudentOrClassroomAccommodationOption = async ({
    rosterItem,
    sectionKeys,
    shouldRemove,
    toggleKey
  } = {}) => {
    const toggleGroupsMap = new Map(config?.toggleGroups || []);
    for (const sectionKey of sectionKeys) {
      const mainAccommodationId = ACCOMMODATION_TOGGLE_KEY[sectionKey][toggleKey];

      const val = toggleGroupsMap?.get(mainAccommodationId) || [];
      const toggleGroupArray = Array.isArray(val) ? val : [];
      if (!toggleGroupArray?.length) {
        // eslint-disable-next-line no-await-in-loop
        await accommodationsManager.updateStudentOrClassroomAccommodationOption(
          rosterItem?.id, mainAccommodationId, shouldRemove
        );
      } else {
        for (const accommodationId of toggleGroupArray) {
          // eslint-disable-next-line no-await-in-loop
          await accommodationsManager.updateStudentOrClassroomAccommodationOption(
            rosterItem?.id, accommodationId, shouldRemove
          );
        }
      }
    }
  };

  const getTableCellAdditionalClassNames = (index) => {
    let additionalClassNames = '';
    if (config?.cellDividerClassName === 'section-divider' && index === 0) {
      additionalClassNames = classNames(additionalClassNames, 'section-divider');
    } else if (config?.cellDividerClassName !== 'section-divider') {
      additionalClassNames = classNames(additionalClassNames, config.cellDividerClassName);
    }
    return additionalClassNames;
  };

  const normalizeTranslationLanguageValue = (value) => {
    if (!value || value === 'All') {
      return 'en';
    }
    // If it's a comma-separated string, take the first value only (single select)
    if (typeof value === 'string' && value.includes(',')) {
      return value.split(',')[0].trim();
    }
    if (Array.isArray(value)) {
      return value[0] || 'en';
    }
    return value;
  };

  const getSelectedTranslationLanguage = (rosterItem) => {
    if (translationLanguageSelections.has(rosterItem.id)) {
      return translationLanguageSelections.get(rosterItem.id);
    }
    return normalizeTranslationLanguageValue(rosterItem.localeId);
  };

  const getTranslationLanguageDisplayValue = (selectedLanguage) => {
    if (!selectedLanguage) {
      return localeNamesMap['en'] || 'English';
    }
    return localeNamesMap[selectedLanguage] || selectedLanguage;
  };

  const handleTranslationLanguageSelect = async ({ rosterItem, language }) => {
    // Only proceed if publisher has C2C Translate (AI) capabilities
    if (!hasC2CTranslate) {
      return;
    }

    // Store previous value for rollback
    const previousSelection = translationLanguageSelections.get(rosterItem.id) ?? 
                              normalizeTranslationLanguageValue(rosterItem.localeId);

    // Optimistically update UI
    setTranslationLanguageSelections((previousSelections) => {
      const nextSelections = new Map(previousSelections);
      nextSelections.set(rosterItem.id, language);
      return nextSelections;
    });

    // Update backend
    try {
      const localeIdToUpdate = language;
      const result = await AccommodationsService.updateUserLocale(rosterItem.id, localeIdToUpdate);
      
      if (!result.success) {
        console.error('Failed to update locale:', result.message);
        
        // Rollback optimistic update on failure
        setTranslationLanguageSelections((previousSelections) => {
          const nextSelections = new Map(previousSelections);
          nextSelections.set(rosterItem.id, previousSelection);
          return nextSelections;
        });
      }
    } catch (error) {
      console.error('Error updating locale:', error);
      
      // Rollback optimistic update on error
      setTranslationLanguageSelections((previousSelections) => {
        const nextSelections = new Map(previousSelections);
        nextSelections.set(rosterItem.id, previousSelection);
        return nextSelections;
      });
    }
  };

  const renderTranslationLanguageCell = ({ rosterItem } = {}) => {
    const selectedLanguage = getSelectedTranslationLanguage(rosterItem);
    const displayValue = getTranslationLanguageDisplayValue(selectedLanguage);

    const trigger = (
      <button
        className='translation-language-trigger clickable link-color'
        type='button'
      >
        {displayValue}
      </button>
    );

    return (
      <Table.Cell className='accommodations-cell popup-cell translationLanguage'>
        {PopupService.renderPopup({
          className: 'translation-language-popup',
          content: (
            <TranslationLanguageSelector
              onSelectLanguage={(language) => handleTranslationLanguageSelect({ language, rosterItem })}
              selectedLanguage={selectedLanguage}
            />
          ),
          on: 'click',
          position: 'bottom left',
          trigger
        })}
      </Table.Cell>
    );
  };

  const renderAccommodationsTable = () => {
    return (hideTable && loadingAccommodationsTable) ? <Loader active /> : (
      <>
        <Loader active={!hideTable && loadingAccommodationsTable} />
        <Table className='accommodations-table' striped>
          {config.showTopHeader && renderAccommodationsTopHeader()}
          <Table.Header>
            {renderAccommodationsTableHeader()}
          </Table.Header>
          <Table.Body>
            {renderAccommodationsTableBody()}
          </Table.Body>
        </Table>
      </>
    );
  };

  const renderAccommodationsTopHeader = () => {
    const assessmentSectionToggleCount = AccommodationsService.getAvailableSectionToggleCount({
      sectionKey: 'assessmentSection'
    });
    const lessonSectionToggleCount = AccommodationsService.getAvailableSectionToggleCount({
      sectionKey: 'lessonSection'
    });
    return (
      <Table.Header>
        <Table.Row className='accommodations-table-top-header'>
          <Table.Cell className='accommodations-cell-first-name firstName placeholder'>
            {/* placeholder */}
          </Table.Cell>
          <Table.Cell className='accommodations-cell-last-name lastName placeholder'>
            {/* placeholder */}
          </Table.Cell>

          {renderAccommodationsTopHeaderSection({
            sectionKey: 'assessmentSection', toggleCount: assessmentSectionToggleCount
          })}
          {renderAccommodationsTopHeaderSection({
            sectionKey: 'lessonSection', toggleCount: lessonSectionToggleCount
          })}
          {hasC2CTranslate && (
            <Table.Cell className='accommodations-cell-translation-language translationLanguage placeholder'>
              {/* placeholder */}
            </Table.Cell>
          )}
        </Table.Row>
      </Table.Header>
    );
  };

  /**
   * @param {{
   *   sectionKey: 'assessmentSection' | 'lessonSection'
   *   toggleCount: number
   * }}
   */
  const renderAccommodationsTopHeaderSection = ({ sectionKey, toggleCount } = {}) => {
    const cells = new Array(toggleCount).fill();

    const firstSectionCellIndex = 0;
    const lastSectionCellIndex = cells.length - 1;
    const sectionCellIndex = sectionKey === 'assessmentSection' ? lastSectionCellIndex : firstSectionCellIndex;
    cells[sectionCellIndex] = (
      <>
        {t(sectionKey)}
        {renderAccommodationsHeaderPopup({ content: t(`${sectionKey}Description`) })}
      </>
    );
    return (
      <>
        {cells.map((cellJsx, index) => {
          let additionalClassNames = getTableCellAdditionalClassNames(index)?.trim?.();
          additionalClassNames = classNames(sectionKey, {
            assessmentSectionLabel: index === firstSectionCellIndex || index === lastSectionCellIndex
          }, additionalClassNames);
          return (
            <Table.Cell key={`placeholder_${sectionKey}_${index}`}
              className={additionalClassNames}>
              {cellJsx}
            </Table.Cell>
          );
        })}
      </>
    );
  };

  const renderAccommodationsTableHeader = () => {
    let tableHeaderClassNames = 'accommodations-header-row';
    tableHeaderClassNames += config?.showTopHeader ? ' has-top-header' : '';
    return (
      <Table.Row className={tableHeaderClassNames} style={{ fontSize: config.tableHeaderFontSize || 'inherit' }}>
        {/* HEADER: FIRST NAME */}
        <Table.HeaderCell className='accommodations-cell-first-name firstName'>
          {t('firstName')}
        </Table.HeaderCell>
        {/* HEADER: LAST NAME */}
        <Table.HeaderCell className='accommodations-cell-last-name lastName'>
          {t('lastName')}
        </Table.HeaderCell>
        {/* HEADER: TOGGLES */}
        {renderAccommodationsTableHeaderRowSection({ sectionKey: 'assessmentSection' })}
        {renderAccommodationsTableHeaderRowSection({ sectionKey: 'lessonSection' })}
        {/* HEADER: TRANSLATION LANGUAGE */}
        {hasC2CTranslate && (
          <Table.HeaderCell className='accommodations-cell-translation-language translationLanguage'>
            {t('translationLanguage')}
          </Table.HeaderCell>
        )}
      </Table.Row>
    );
  };

  /**
   * @param {{ sectionKey: 'assessmentSection' | 'lessonSection' }}
   */
  const renderAccommodationsTableHeaderRowSection = ({ sectionKey } = {}) => {
    return config[sectionKey].show && (
      Object.keys(config[sectionKey].toggles).map((toggleKey, index) => {
        const infoIconLabelTranslationKey = `${toggleKey}InfoIconLabel`; // e.g. 'contentLanguageToggleInfoIconLabel'
        const infoIconLabelTranslationValue = t(infoIconLabelTranslationKey, ''); // e.g. 'Coming Fall 2024'

        // only show an info icon for a toggle label if it has a proper translation value associated with it
        // else if `infoIconLabelTranslationValue` is empty or equals the translation key, do not show the info icon
        const hasInfoIcon = infoIconLabelTranslationValue && (
          infoIconLabelTranslationKey !== infoIconLabelTranslationValue
        );

        const additionalClassNames = getTableCellAdditionalClassNames(index);
        return config[sectionKey].toggles[toggleKey] && (
          <Table.HeaderCell key={toggleKey}
            className={classNames(
              'accommodations-header-cell',
              `accommodations-cell-${index}`,
              sectionKey,
              toggleKey,
              additionalClassNames
            )}>
            {t(toggleKey)}
            {hasInfoIcon && renderAccommodationsHeaderPopup({ content: infoIconLabelTranslationValue })}
          </Table.HeaderCell>
        );
      })
    );
  };

  const renderAccommodationsHeaderPopup = ({ content }) => {
    return PopupService.renderPopup({
      className: 'toolbar-item-popup',
      content,
      trigger: <Image alt='' className='info-icon-pointable' spaced src={iconInfo} />
    });
  };

  const renderAccommodationsTableBody = () => {
    return (
      <>
        {accommodationsManager.currentAccommodationsRosterArray?.length ? (
          <Table.Row>
            {/* BODY: TOP (summary) ROW */}
            <Table.Cell>{t('selectAllStudents')}</Table.Cell>
            <Table.Cell>{/* placeholder */}</Table.Cell>
            {renderAccommodationsTableBodySummaryRowSection({ sectionKey: 'assessmentSection' })}
            {renderAccommodationsTableBodySummaryRowSection({ sectionKey: 'lessonSection' })}
            {hasC2CTranslate && <Table.Cell>{/* placeholder */}</Table.Cell>}
          </Table.Row>
        ) : (
          <Table.Row>
            <Table.Cell className='accommodations-cell zeroState'>{t('accommodationsZeroState')}</Table.Cell>
          </Table.Row>
        )}
        {/* BODY: STUDENT ROWS */}
        {accommodationsManager.currentAccommodationsRosterArray?.map((rosterItem) => {
          return (
            <Table.Row key={rosterItem.id}>
              {renderTableBodyPopupCell({ cellKey: 'firstName', content: rosterItem.firstName })}
              {renderTableBodyPopupCell({ cellKey: 'lastName', content: rosterItem.lastName })}
              {renderAccommodationsTableBodyRowSection({ rosterItem, sectionKey: 'assessmentSection' })}
              {renderAccommodationsTableBodyRowSection({ rosterItem, sectionKey: 'lessonSection' })}
              {hasC2CTranslate && renderTranslationLanguageCell({ rosterItem })}
            </Table.Row>
          );
        })}
      </>
    );
  };

  /**
   * @param {{ sectionKey: 'assessmentSection' | 'lessonSection' }}
   */
  const renderAccommodationsTableBodySummaryRowSection = ({ sectionKey } = {}) => {
    if (!config[sectionKey].show) {
      return null;
    }
    const toggleKeys = AccommodationsService.getConfigEnabledAccommodationToggleKeys({
      config, sectionKey
    });

    const rosterItems = accommodationsManager.currentAccommodationsRosterArray;

    const offAccommodationsForAll = AccommodationsService.getOffAccommodationsForAll({
      config, rosterItems, sectionKey
    });

    return toggleKeys.map((toggleKey, index) => {
      const additionalClassNames = getTableCellAdditionalClassNames(index);
      const isAccommodationOn = !offAccommodationsForAll.includes(toggleKey);
      return (
        <Table.Cell key={`all_${toggleKey}`}
          className={classNames(
            'accommodations-cell',
            `accommodations-cell-${index}`,
            'toggle',
            sectionKey,
            toggleKey,
            additionalClassNames
          )}>
          {renderToggler({
            checked: isAccommodationOn,
            sectionKey,
            toggleKey
          })}
        </Table.Cell>
      );
    });
  };

  const renderTableBodyPopupCell = ({ cellKey, content } = {}) => {
    return (
      <Table.Cell className={`accommodations-cell popup-cell ${cellKey}`}>
        {PopupService.renderPopup({
          content,
          offset: [-11, 0],
          position: 'top left',
          trigger: content
        })}
      </Table.Cell>
    );
  };

  /**
   * @param {{ sectionKey: 'assessmentSection' | 'lessonSection' }}
   */
  const renderAccommodationsTableBodyRowSection = ({ rosterItem, sectionKey } = {}) => {
    return config[sectionKey].show && (
      Object.keys(config[sectionKey].toggles).filter((toggleKey) => {
        return config[sectionKey].toggles[toggleKey];
      }).map((toggleKey, index) => {
        const additionalClassNames = getTableCellAdditionalClassNames(index);
        const isAccommodationOn = !rosterItem.offAccommodations?.includes(
          ACCOMMODATION_TOGGLE_KEY[sectionKey][toggleKey]
        );
        return (
          <Table.Cell key={toggleKey}
            className={classNames(
              'accommodations-cell',
              `accommodations-cell-${index}`,
              'toggle',
              sectionKey,
              toggleKey,
              additionalClassNames
            )}>
            {renderToggler({
              checked: isAccommodationOn,
              rosterItem,
              sectionKey,
              toggleKey
            })}
          </Table.Cell>
        );
      })
    );
  };

  const isTextToSpeechEnabled = ({ rosterItem, sectionKey } = {}) => {
    if (rosterItem) {
      return !rosterItem.offAccommodations?.includes(
        ACCOMMODATION_TOGGLE_KEY[sectionKey].textToSpeechToggle
      );
    }
    return !accommodationsManager.currentAccommodationsRosterArray.some((item) => {
      return item.offAccommodations?.includes(
        ACCOMMODATION_TOGGLE_KEY[sectionKey].textToSpeechToggle
      );
    });
  };

  const renderToggler = ({ checked, rosterItem, sectionKey, toggleKey } = {}) => {
    // Enable translationToggle and dictionaryToggle when textToSpeechToggle is enabled
    let finalShouldDisable = false;
    if (accommodationsManager.wordToolsTTSDependent) {
      const shouldDisable = !isTextToSpeechEnabled({ rosterItem, sectionKey }) &&
      (toggleKey === 'translationToggle' || toggleKey === 'dictionaryToggle');
      finalShouldDisable = shouldDisable;
    }

    return (
      <div className={`accommodations-table-column-toggler ${toggleKey}`}>
        <div className='toggle-on-or-off-wrapper'>
          <Checkbox
            className='on-or-off-toggler'
            defaultChecked={checked}
            disabled={finalShouldDisable}
            onChange={(event, { checked }) => handleToggleAccommodation(event, { checked }, {
              rosterItem, sectionKey, toggleKey
            })}
            toggle />
        </div>
      </div>
    );
  };
  return renderAccommodationsTable();
});
export default AccommodationsTable;

SatCoreRegister('AccommodationsTable', AccommodationsTable);
